import type { UpgradeFulfillment, UpgradeSession, UpgradeTargetPlan } from "../api/contract";
import { confirmUpgradeFulfillment } from "../api/endpoints";

// The Stripe return carries only the intent id and the claim, and the fulfillment read
// reports the customer's *current* plan — for a canceled or expired intent that is the
// pre-upgrade plan. The target and the mode are only known where the session was created,
// so they are kept in sessionStorage (same tab, survives the hosted-page round trip, never
// sent anywhere) so the return path can report `billing.upgrade_result` truthfully
// (TUFF_026). A different tab or browser has no memory and emits nothing rather than a guess.
const INTENT_STORAGE_KEY = "epidev.upgrade.intent";

export type UpgradeResultTarget = UpgradeTargetPlan;
const RESULT_TARGETS: readonly UpgradeResultTarget[] = ["pro", "team", "builders"];
export type UpgradeResultOutcome = "pending" | "fulfilled" | "canceled" | "expired" | "failure";

export interface RememberedUpgradeIntent {
  intentId: string;
  targetPlan: UpgradeResultTarget;
  mode: UpgradeSession["mode"];
}

export function rememberUpgradeIntent(
  session: Pick<UpgradeSession, "intentId" | "mode">,
  targetPlan: UpgradeResultTarget
): void {
  try {
    const remembered: RememberedUpgradeIntent = { intentId: session.intentId, targetPlan, mode: session.mode };
    window.sessionStorage.setItem(INTENT_STORAGE_KEY, JSON.stringify(remembered));
  } catch {
    // Storage denied (private mode, blocked cookies): the upgrade still proceeds; only the
    // result event goes unreported.
  }
}

export function recallUpgradeIntent(intentId: string): RememberedUpgradeIntent | null {
  try {
    const raw = window.sessionStorage.getItem(INTENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RememberedUpgradeIntent> | null;
    if (!parsed || parsed.intentId !== intentId) return null;
    if (!RESULT_TARGETS.includes(parsed.targetPlan as UpgradeResultTarget)) return null;
    if (parsed.mode !== "checkout" && parsed.mode !== "subscription_update") return null;
    return { intentId, targetPlan: parsed.targetPlan as UpgradeResultTarget, mode: parsed.mode };
  } catch {
    return null;
  }
}

export function forgetUpgradeIntent(): void {
  try {
    window.sessionStorage.removeItem(INTENT_STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}

/** Maps a fulfillment poll result (null = still pending after the last attempt) onto the catalogued outcome. */
export function upgradeResultOutcome(fulfillment: UpgradeFulfillment | null): UpgradeResultOutcome {
  return fulfillment ? fulfillment.status : "pending";
}

export interface UpgradeFulfillmentPoll {
  orgId: string;
  intentId: string;
  claim: string;
  signal: AbortSignal;
  attempts?: number;
  intervalMs?: number;
  pause?: (milliseconds: number, signal: AbortSignal) => Promise<void>;
}

function defaultPause(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export async function pollUpgradeFulfillment({
  orgId,
  intentId,
  claim,
  signal,
  attempts = 7,
  intervalMs = 2_500,
  pause = defaultPause
}: UpgradeFulfillmentPoll): Promise<UpgradeFulfillment | null> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const fulfillment = await confirmUpgradeFulfillment(orgId, { intentId, claim }, signal);
    if (fulfillment.status !== "pending") return fulfillment;
    if (attempt < attempts - 1) await pause(intervalMs, signal);
  }
  return null;
}
